import Button from 'components/button/Button';
import { ValidationCodeField } from 'components/form/ValidationCodeField';
import { Spinner } from 'components/spinner/Spinner';
import { ThemedLink } from 'components/themedLink/ThemedLink';
import { ERROR_CODES, getErrorMessage } from 'errors';
import { Form, Formik } from 'formik';
import { Toast, ToastMessageReason } from 'helpers/toast';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { AuthType, RegisterStep, TFAVerificationPayload, TFAVerificationProps } from './models';
import { postEnableTfa, postSmsTFASetup } from './RegisterApi';
import { handleSMSRequestError, handleTFAError } from './RegistrationErrorHandling';

export const TFAVerification: React.FC<TFAVerificationProps> = ({
    translations,
    accountDetails,
    onComplete,
    authType,
    setAuthType,
    phoneNumber,
    sharedSecret,
    setRegistrationStep,
    skipApi = false,
}) => {
    const { t } = useTranslation('auth');
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const dispatch = useDispatch();
    const initialValues: TFAVerificationPayload = {
        username: accountDetails && accountDetails.emailAddress,
        password: accountDetails && accountDetails.password,
        tfaType: authType === AuthType.APP ? 'AuthenticatorApp' : 'SMS',
        tfaCode: '',
        phoneNumber: phoneNumber ? phoneNumber : '',
        totpSharedSecret: sharedSecret ? sharedSecret : '',
    };

    //Send SMS verification code on mount (skipped when skipApi - new registration flow)
    React.useEffect(() => {
        if (skipApi) return;
        if (phoneNumber && accountDetails) {
            (async () => {
                const result = await postSmsTFASetup({
                    username: accountDetails.emailAddress,
                    password: accountDetails.password,
                    phoneNumber,
                    bNewPhoneNumber: true,
                });
                if (result.response) {
                }
                if (result.errors && result.errors.length > 0) {
                    const translatableErrors = result.errors.filter(
                        (err) => err.messageCode in ERROR_CODES
                    );
                    if (translatableErrors.length > 0) {
                        Toast.openToastMessage(
                            getErrorMessage(translatableErrors[0].messageCode),
                            ToastMessageReason.ERROR
                        );
                    } else {
                        Toast.openGenericErrorToast();
                    }
                }
            })();
        }
    }, [phoneNumber, accountDetails, dispatch, skipApi]);

    //Modal functionality (resend skipped when skipApi - new registration flow)
    const handleResendCode = () => {
        if (skipApi) return;
        if (phoneNumber && accountDetails) {
            (async () => {
                const result = await postSmsTFASetup({
                    username: accountDetails.emailAddress,
                    password: accountDetails.password,
                    phoneNumber,
                    bNewPhoneNumber: true,
                });
                if (result.response) {
                    Toast.openToastMessage(
                        t('verificationCodeSentTo', { phoneNumber }),
                        ToastMessageReason.PENDING
                    );
                }
                if (result.errors && result.errors.length > 0) {
                    setError(handleSMSRequestError(result.errors[0]));
                }
            })();
        }
    };

    const handleUseSMS = () => {
        setAuthType(AuthType.SMS);
        setRegistrationStep(RegisterStep.TFA_SETUP);
    };

    const handleSubmit = (values: TFAVerificationPayload) => {
        if (skipApi) {
            onComplete();
            return;
        }
        setLoading(true);
        (async () => {
            const result = await postEnableTfa(values);
            if (result.response) {
                onComplete();
            }
            if (result.errors && result.errors.length > 0) {
                setError(handleTFAError(result.errors[0]));
            }
            setLoading(false);
        })();
    };

    const handleResetError = () => {
        setError(null);
    };

    return (
        <>
            {loading && <Spinner />}
            {translations && (
                <>
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        enableReinitialize
                    >
                        {(formikProps) => {
                            return (
                                <Form onChange={handleResetError}>
                                    <div className="RegisterFormWidth">
                                        <Button
                                            priority="tertiary"
                                            className="ChangeNumberButton InlineLink notranslate"
                                            onClick={() =>
                                                setRegistrationStep(RegisterStep.TFA_SETUP)
                                            }
                                            type="button"
                                        >
                                            {t('back', { ns: 'common' })}
                                        </Button>
                                        <h1 className="notranslate" translate="no">{t('twoStepVerification')}</h1>
                                        <p className="notranslate" translate="no">
                                            {authType === AuthType.APP
                                                ? t('authenticatorSubheading')
                                                : t('smsSubheading')}
                                        </p>
                                        <ValidationCodeField
                                            field={'tfaCode'}
                                            label={t('verificationCode')}
                                            required={true}
                                            autoFocus
                                            holdFocus
                                            value={formikProps.values.tfaCode}
                                        >
                                            {authType === AuthType.APP ? (
                                                <ThemedLink
                                                    onClick={handleUseSMS}
                                                    className="RegisterLink notranslate"
                                                >
                                                    {t('useSmsInstead')}
                                                </ThemedLink>
                                            ) : (
                                                <ThemedLink
                                                    onClick={handleResendCode}
                                                    className="RegisterLink notranslate"
                                                >
                                                    {t('resendCode')}
                                                </ThemedLink>
                                            )}
                                        </ValidationCodeField>

                                        {error && <div className="ErrorMessage">{error}</div>}

                                        <Button
                                            priority="primary"
                                            variety="full"
                                            type="submit"
                                            disabled={loading}
                                            color="second"
                                            className="notranslate"
                                        >
                                            {t('continue', { ns: 'common' })}
                                        </Button>
                                    </div>
                                </Form>
                            );
                        }}
                    </Formik>
                </>
            )}
        </>
    );
};
